import {view, Action, Tooltip, Badge} from '@sail/ui';
import {useIntl} from 'react-intl';
import {noTranslateMessages} from '../helpers/noTranslate';
import {widgetLinkAction} from '../helpers/widgetLinkAction';
import {
  useWidgetConversationId,
  useWidgetRoutingMode,
} from '../Widget/useWidget';

const QA_ADMIN_URL = 'https://admin.qa.corp.stripe.com';
const PROD_ADMIN_URL = 'https://admin.corp.stripe.com';
const CONVERSATION_PATH = '/universal-help/conversation-details/v2/';

const devMessages = noTranslateMessages({
  badgeLabel: {
    id: 'conversations_ui.header.dev_badge_label',
    defaultMessage: 'Dev',
  },
  normalRoutingModeLabel: {
    id: 'support_conversations_client.header.dev_badge.normal_routing_mode_label',
    defaultMessage: 'Routing mode: normal',
  },
  testQueueRoutingModeLabel: {
    id: 'support_conversations_client.header.dev_badge.test_queue_routing_mode_label',
    defaultMessage: 'Routing mode: test queue',
  },
  syntheticsModeLabel: {
    id: 'support_conversations_client.header.dev_badge.synthetics_mode_label',
    defaultMessage: 'Synthetics',
  },
  conversationTranscriptLabel: {
    id: 'support_conversations_client.header.dev_badge.conversation_transcript_label',
    defaultMessage: 'Transcript',
  },
  forestVersionLabel: {
    id: 'support_conversations_client.header.dev_badge.forest_version_label',
    defaultMessage: 'Forest version: {forestVersion}',
  },
  testAgentModeLabel: {
    id: 'support_conversations_client.header.dev_badge.test_agent_mode_label',
    defaultMessage: 'Test agent mode: {testAgentMode}',
  },
});
function showDevBadge(
  url: URL,
  routingMode: string | undefined,
  forestVersion: string,
  testAgentMode: string,
) {
  const host = url.host.toLowerCase();
  if (
    host.endsWith('-mydev.dev.stripe.me') ||
    host.startsWith('qa-support') ||
    forestVersion !== 'off' ||
    testAgentMode !== 'off' ||
    routingMode === 'dev'
  ) {
    return true;
  }

  return false;
}

function generateTranscriptLink(url: URL, conversationId?: string) {
  const host = url.host.toLowerCase();
  if (host.endsWith('-mydev.dev.stripe.me')) {
    const adminOrigin = url.origin.replace(
      /--support_conversations-/,
      '--admin-',
    );
    return `${adminOrigin}${CONVERSATION_PATH}${conversationId}`;
  }
  if (host.startsWith('qa-support')) {
    return `${QA_ADMIN_URL}${CONVERSATION_PATH}${conversationId}`;
  }
  return `${PROD_ADMIN_URL}${CONVERSATION_PATH}${conversationId}`;
}

export function DevBadge() {
  const {conversationId} = useWidgetConversationId();
  const {routingMode} = useWidgetRoutingMode();
  const intl = useIntl();
  const url = new URL(window.location.href);
  const isTestQueue = routingMode === 'dev' || routingMode === 'synthetic';
  const urlSearchParams = new URLSearchParams(window.location.search);
  const forestVersion = urlSearchParams.get('forestVersion') || 'off';
  const testAgentMode = urlSearchParams.get('test_agent_mode') || 'off';

  if (!showDevBadge(url, routingMode, forestVersion, testAgentMode)) {
    if (routingMode === 'synthetic') {
      return (
        <Badge type="info" data-testid="dev-badge">
          {intl.formatMessage(devMessages.syntheticsModeLabel)}
        </Badge>
      );
    }

    return null;
  }

  const transcriptLink = generateTranscriptLink(url, conversationId);

  return (
    <Tooltip
      trigger={
        <Badge type="info" data-testid="dev-badge">
          {intl.formatMessage(devMessages.badgeLabel)}
        </Badge>
      }
    >
      <view.div css={{stack: 'y', gap: 'small'}}>
        {conversationId ? (
          <Action href={transcriptLink} uses={[widgetLinkAction()]}>
            {intl.formatMessage(devMessages.conversationTranscriptLabel)}
          </Action>
        ) : null}
        <view.div>
          {routingMode === 'normal'
            ? intl.formatMessage(devMessages.normalRoutingModeLabel)
            : null}
          {isTestQueue
            ? intl.formatMessage(devMessages.testQueueRoutingModeLabel)
            : null}
        </view.div>
        <view.div>
          {intl.formatMessage(devMessages.forestVersionLabel, {
            forestVersion,
          })}
        </view.div>
        <view.div>
          {intl.formatMessage(devMessages.testAgentModeLabel, {
            testAgentMode,
          })}
        </view.div>
      </view.div>
    </Tooltip>
  );
}
